import { useState, FC, ReactNode } from 'react';
import {
  CheckCheck,
  Ticket,
  Copy,
  Check,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import { Badge } from '../ui/Badge';

export interface WhatsAppBubblePreviewProps {
  imageUrl?: string | null;
  title?: string;
  text: string;
  onTextChange?: (newText: string) => void;
  isEditable?: boolean;
  originalPrice?: number | null;
  promotionalPrice?: number | null;
  couponCode?: string | null;
  isCouponOnly?: boolean;
  timestamp?: string;
  marketplace?: string;
  groupName?: string;
  className?: string;
}

/**
 * Converte marcação do WhatsApp (*negrito*, ~tachado~, _itálico_, links) em elementos React seguros.
 */
export function renderFormattedWhatsAppText(rawText: string): ReactNode[] {
  if (!rawText) return [];

  const lines = rawText.split('\n');

  return lines.map((line, lineIdx) => {
    const isQuote = line.trimStart().startsWith('>');
    const contentLine = isQuote
      ? line.trimStart().substring(1).trimStart()
      : line;

    // Regex simples para capturar blocos especiais
    const parts: ReactNode[] = [];
    let keyIdx = 0;

    // Processa tokens de linha
    const tokenRegex = /(\*[^*]+\*|~[^~]+~|_[^_]+_|https?:\/\/[^\s]+)/g;
    let match: RegExpExecArray | null;
    let lastIndex = 0;

    while ((match = tokenRegex.exec(contentLine)) !== null) {
      // Texto anterior ao token
      if (match.index > lastIndex) {
        parts.push(
          <span key={`${lineIdx}-${keyIdx++}`}>
            {contentLine.substring(lastIndex, match.index)}
          </span>
        );
      }

      const token = match[0];
      if (token.startsWith('*') && token.endsWith('*')) {
        parts.push(
          <strong
            key={`${lineIdx}-${keyIdx++}`}
            className="font-bold text-content-main"
          >
            {token.slice(1, -1)}
          </strong>
        );
      } else if (token.startsWith('~') && token.endsWith('~')) {
        parts.push(
          <del
            key={`${lineIdx}-${keyIdx++}`}
            className="line-through opacity-70"
          >
            {token.slice(1, -1)}
          </del>
        );
      } else if (token.startsWith('_') && token.endsWith('_')) {
        parts.push(
          <em key={`${lineIdx}-${keyIdx++}`} className="italic">
            {token.slice(1, -1)}
          </em>
        );
      } else if (token.startsWith('http')) {
        parts.push(
          <span
            key={`${lineIdx}-${keyIdx++}`}
            className="font-medium text-brand-600 underline dark:text-brand-400"
          >
            {token}
          </span>
        );
      }

      lastIndex = tokenRegex.lastIndex;
    }

    if (lastIndex < contentLine.length) {
      parts.push(
        <span key={`${lineIdx}-${keyIdx++}`}>
          {contentLine.substring(lastIndex)}
        </span>
      );
    }

    return (
      <span
        key={lineIdx}
        className={`block min-h-4 ${
          isQuote
            ? 'bg-surface-subtle/60 my-1 rounded-r border-l-2 border-brand-500/70 py-0.5 pl-2.5 italic text-content-main'
            : ''
        }`}
      >
        {parts.length > 0 ? parts : ' '}
      </span>
    );
  });
}

export const WhatsAppBubblePreview: FC<WhatsAppBubblePreviewProps> = ({
  imageUrl,
  title,
  text,
  onTextChange,
  isEditable = false,
  originalPrice,
  promotionalPrice,
  couponCode,
  isCouponOnly = false,
  timestamp,
  marketplace = 'other',
  groupName = 'Achadinhos VIP 🔥',
  className = '',
}) => {
  const [copiedCoupon, setCopiedCoupon] = useState(false);
  const [imageError, setImageError] = useState(false);

  const displayTime =
    timestamp ||
    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const handleCopyCoupon = () => {
    if (!couponCode) return;
    navigator.clipboard.writeText(couponCode);
    setCopiedCoupon(true);
    setTimeout(() => setCopiedCoupon(false), 2000);
  };

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-2xl border border-surface-border bg-surface-panel shadow-sm ${className}`}
    >
      {/* 1. Header do Chat Simulado do WhatsApp */}
      <div className="flex items-center justify-between border-b border-surface-border bg-surface-subtle px-3.5 py-2.5">
        <div className="flex items-center gap-2.5 overflow-hidden">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-slate-950">
            <Smartphone className="h-4 w-4 stroke-slate-950" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="truncate text-xs font-bold text-content-main">
              {groupName}
            </h4>
            <p className="truncate text-2xs text-content-muted">
              WhatsApp Web • Visualização em Tempo Real
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {marketplace !== 'other' && (
            <Badge variant="neutral" className="font-mono text-2xs uppercase">
              {marketplace}
            </Badge>
          )}
          <span className="flex h-2 w-2 animate-pulse rounded-full bg-brand-500" />
        </div>
      </div>

      {/* 2. Área do Canvas do Chat (Papel de parede suave) */}
      <div className="flex min-h-64 flex-1 flex-col justify-end overflow-y-auto bg-surface-canvas p-3 sm:p-4">
        {/* Balão de Mensagem de Saída (Verde WhatsApp com contraste semântico) */}
        <div className="rounded-tr-xs shadow-xs ml-auto w-full max-w-sm rounded-2xl border border-brand-500/20 bg-brand-50/70 p-3 text-xs leading-relaxed dark:border-brand-500/25 dark:bg-brand-950/60 sm:max-w-md">
          {/* Mídia da Oferta (Imagem do Produto) */}
          {imageUrl && !imageError && (
            <div className="relative mb-2.5 overflow-hidden rounded-xl border border-surface-border bg-surface-panel">
              <img
                src={imageUrl}
                alt={title || 'Produto em Destaque'}
                onError={() => setImageError(true)}
                className="max-h-56 w-full object-contain p-1"
                loading="lazy"
              />
              {originalPrice &&
                promotionalPrice &&
                originalPrice > promotionalPrice && (
                  <div className="absolute right-2 top-2">
                    <Badge variant="online" className="shadow-xs font-bold">
                      {`-${Math.round(
                        (1 - promotionalPrice / originalPrice) * 100
                      )}% OFF`}
                    </Badge>
                  </div>
                )}
            </div>
          )}

          {/* Preço formatado quando não há imagem */}
          {(!imageUrl || imageError) && promotionalPrice && (
            <div className="bg-surface-panel/90 dark:bg-surface-subtle/90 mb-2.5 flex items-center justify-between rounded-xl border border-surface-border p-2.5">
              <div className="flex items-center gap-2">
                <span className="font-bold text-brand-600 dark:text-brand-400">
                  {`R$ ${promotionalPrice.toFixed(2).replace('.', ',')}`}
                </span>
                {originalPrice && originalPrice > promotionalPrice && (
                  <span className="text-2xs text-content-muted line-through">
                    {`R$ ${originalPrice.toFixed(2).replace('.', ',')}`}
                  </span>
                )}
              </div>
              {originalPrice && originalPrice > promotionalPrice && (
                <Badge
                  variant="online"
                  className="shadow-xs text-micro font-bold"
                >
                  {`-${Math.round((1 - promotionalPrice / originalPrice) * 100)}% OFF`}
                </Badge>
              )}
            </div>
          )}

          {/* Destaque de Cupom quando aplicável */}
          {(isCouponOnly || couponCode) && (
            <div className="bg-surface-panel/80 dark:bg-surface-subtle/80 mb-2.5 flex items-center justify-between rounded-xl border border-dashed border-brand-500/40 p-2.5">
              <div className="flex items-center gap-2">
                <Ticket className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                <div className="space-y-0.5">
                  <span className="block font-mono text-xs font-bold tracking-wider text-content-main">
                    {couponCode || 'CUPOM'}
                  </span>
                  <span className="block text-2xs text-content-muted">
                    {isCouponOnly
                      ? 'Oferta exclusiva de cupom'
                      : 'Cupom promocional'}
                  </span>
                </div>
              </div>
              {couponCode && (
                <button
                  type="button"
                  onClick={handleCopyCoupon}
                  title="Copiar cupom"
                  className="cursor-pointer rounded-lg p-1.5 text-content-muted transition-colors hover:bg-surface-subtle hover:text-content-main"
                >
                  {copiedCoupon ? (
                    <Check className="h-3.5 w-3.5 text-brand-600 dark:text-brand-400" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
            </div>
          )}

          {/* Corpo do Texto da Mensagem */}
          {isEditable && onTextChange ? (
            <textarea
              value={text}
              onChange={(e) => onTextChange(e.target.value)}
              rows={8}
              className="focus:outline-hidden w-full resize-none rounded-lg border border-surface-border bg-surface-panel p-2 font-mono text-xs text-content-main placeholder:text-content-muted focus:border-brand-500"
              placeholder="Digite ou edite a mensagem do WhatsApp..."
            />
          ) : (
            <div className="select-text space-y-0.5 break-words font-sans text-xs text-content-main">
              {renderFormattedWhatsAppText(text)}
            </div>
          )}

          {/* Rodapé do Balão: Timestamp e Duplo Checkmark */}
          <div className="mt-1.5 flex items-center justify-end gap-1 text-2xs text-content-muted">
            <span className="font-mono text-micro">{displayTime}</span>
            <CheckCheck className="h-3.5 w-3.5 text-sky-500 dark:text-sky-400" />
          </div>
        </div>
      </div>

      {/* 3. Rodapé Operacional com Dica */}
      <div className="flex items-center justify-between border-t border-surface-border bg-surface-subtle px-3.5 py-2 text-2xs text-content-muted">
        <span className="flex items-center gap-1">
          <Sparkles className="h-3 w-3 text-brand-600 dark:text-brand-400" />
          Preview sincronizado em tempo real
        </span>
        <span className="font-mono">WhatsApp Web v2.3</span>
      </div>
    </div>
  );
};
